import React, { useContext, useEffect, useState } from 'react';
import { Camera, Save, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axiosInstance from '../api/AxiosInstance';
import { endpoints } from '../services/endpoints';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';

const AdminProfile = () => {
    const navigate = useNavigate();
    const { fetchProfile } = useContext(AuthContext);
    const { response: profileResponse } = useSelector((state) => state.profileData);
    const profile = profileResponse?.data || {};

    const [form, setForm] = useState({
        username: '',
    });
    const [profileImage, setProfileImage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showClose, setShowClose] = useState(false);

    useEffect(() => {
        setForm({
            username: profile.username || '',
        });
    }, [profile.username]);

    const [previewUrl, setPreviewUrl] = useState('/assets/images/dummy-user.png');

    useEffect(() => {
        if (!profileImage) {
            setPreviewUrl(profile.profilePic || '/assets/images/dummy-user.png');
            return undefined;
        }

        const objectUrl = URL.createObjectURL(profileImage);
        setPreviewUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [profileImage, profile.profilePic]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);

        try {
            const payload = new FormData();
            Object.entries(form).forEach(([key, value]) => payload.append(key, value));
            if (profileImage) payload.append('profile_image', profileImage);

            await axiosInstance.put(endpoints.updateProfile, payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            await fetchProfile();
            setProfileImage(null);
            setShowClose(true);
            showSuccessToast('Profile updated successfully');
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="page_top_section">
                <div className="page-header">
                    <div className="page-header-left d-flex align-items-center">
                        <div className="page-header-title">
                            <nav className="breadcrumb_nav mb-3">
                                <ul className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">Admin</li>
                                    <li className="breadcrumb-item text-capitalize">Profile Details</li>
                                </ul>
                            </nav>
                            <h1 className="fs-3 text-black fw-semibold mb-0 text-capitalize">Profile Details</h1>
                            <p className="text-clr mb-0">Update your admin username and photo</p>
                        </div>
                    </div>
                </div>
            </div>

            <section className="main-section common_section spacer">
                <div className="container">
                    <form className="outline_blk admin-profile-form position-relative" onSubmit={handleSubmit}>
                        {showClose && (
                            <button className="profile-close-btn" type="button" onClick={() => navigate('/admin-dashboard')} aria-label="Close">
                                <X />
                            </button>
                        )}

                        <div className="admin-profile-cover">
                            <div className="user_preview_cover">
                                <img src={previewUrl} alt="Admin profile" />
                            </div>
                            <label className="outline-btn profile-image-btn">
                                <Camera />
                                <span className="btntext">Update Photo</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={(event) => setProfileImage(event.target.files?.[0] || null)}
                                />
                            </label>
                        </div>

                        <div className="row gy-4">
                            <div className="col-md-6">
                                <label className="form-label">Username</label>
                                <input className="form-control" name="username" value={form.username} onChange={handleChange} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Email</label>
                                <input className="form-control" value={profile.email || ''} disabled />
                            </div>
                        </div>

                        <div className="mt-4">
                            <button className="primary-btn" type="submit" disabled={saving}>
                                <Save />
                                <span className="btntext">{saving ? 'Saving...' : 'Save Changes'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </>
    );
};

export default AdminProfile;
